import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '$store/rootReducer';
import { attachActions, attachTimerSelector } from '$store/attach';
import { IcClose16 } from '$assets';
import { useCallback } from 'react';
import { classNames, format, parse } from '$utils';
import { Input, Select } from '$uikit';
import { roundToNearestMinutes } from 'date-fns';
import { useTranslator } from '$hooks';

function makeTime(date: Date, hour: number, minute: number) {
  date.setHours(hour, minute);
  let time = date.getTime();
  time = Math.max(time, Date.now() + 10 * 60 * 1000);
  time = roundToNearestMinutes(time, {
    nearestTo: 5,
  }).getTime();

  return time;
}

export default function Timer({ pickerId }: { pickerId: string }) {
  const timer = useSelector((state: RootState) => attachTimerSelector(state, pickerId));
  const dispatch = useDispatch();
  const t = useTranslator();

  const handleRemove = useCallback(() => {
    dispatch(
      attachActions.setTimer({
        pickerId,
        time: null,
      }),
    );
  }, [dispatch, pickerId]);

  const hour = useMemo(() => {
    if (!timer) {
      return '';
    }

    return format(timer, 'HH', new Date());
  }, [timer]);

  const minute = useMemo(() => {
    if (!timer) {
      return '';
    }

    return format(timer, 'mm', new Date());
  }, [timer]);

  const onChange = useCallback(
    (e) => {
      const date = new Date(new Date(e.target.value).toISOString());

      dispatch(
        attachActions.setTimer({
          pickerId,
          time: makeTime(date, +hour, +minute),
        }),
      );
    },
    [dispatch, hour, minute],
  );

  const value = useMemo(() => {
    if (!timer) {
      return '';
    }

    return format(timer, 'yyyy-MM-dd', new Date());
  }, [timer]);

  const minDate = useMemo(() => {
    if (!timer) {
      return '';
    }

    return format(new Date(), 'yyyy-MM-dd', new Date());
  }, [timer]);

  const maxDate = useMemo(() => {
    if (!timer) {
      return '';
    }

    return format(Date.now() + 86400 * 30 * 3 * 1000, 'yyyy-MM-dd', new Date());
  }, [timer]);

  const hours = useMemo(() => {
    const result: string[] = [];
    for (let i = 0; i <= 23; i++) {
      let val: string = `${i}`;
      if (i < 10) {
        val = `0${i}`;
      }

      result.push(val);
    }

    return result;
  }, []);

  const minutes = useMemo(() => {
    const result: string[] = [];
    for (let i = 0; i <= 55; i += 5) {
      let val: string = `${i}`;
      if (i < 10) {
        val = `0${i}`;
      }

      result.push(val);
    }

    return result;
  }, []);

  const handleHours = useCallback(
    (e) => {
      dispatch(
        attachActions.setTimer({
          pickerId,
          time: makeTime(new Date(timer), +e.target.value, +minute),
        }),
      );
    },
    [dispatch, pickerId, timer, minute],
  );

  const handleMinutes = useCallback(
    (e) => {
      dispatch(
        attachActions.setTimer({
          pickerId,
          time: makeTime(new Date(timer), +hour, +e.target.value),
        }),
      );
    },
    [dispatch, pickerId, timer, hour],
  );

  if (!timer) {
    return null;
  }

  return (
    <div
      className={classNames({
        AttachPickerManager__link: true,
      })}
    >
      <div className="AttachPickerManager__link_header">
        <div className="AttachPickerManager__link_header_title">
          {t('new_post_attach_timer')}
        </div>
        <div className="AttachPickerManager__link_header_remove" onClick={handleRemove}>
          <IcClose16 />
        </div>
      </div>
      <div className="AttachPickerManager__timer">
        <Input
          type="date"
          onChange={onChange}
          value={value}
          // @ts-ignore
          min={minDate}
          max={maxDate}
        />
        <div className="AttachPickerManager__timer__time">
          <Select onChange={handleHours} value={hour}>
            {hours.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
          <span>:</span>
          <Select onChange={handleMinutes} value={minute}>
            {minutes.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
}
